import React, { ChangeEvent, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FontAwesomeIcon from '../../../../ui/font-awesome-icon/font-awesome-icon';
import '../../../../images/flags/4x3/de.svg';
import translation from '../../../../languages';
import { useAppSelector } from '../../../../store';
import Alert from '../../../../ui/alert/alert';
import cn from '../../../../utils/classnames';
import emailHash from '../../../../utils/email-hash';
import enhancedEcommerce from '../../../../utils/enhanced-ecommerce';
import session from '../../../../utils/session';
import { useAutocompleteInvoice, useCheckEmail, getActivePaymentMethodKey } from '../../payment.helpers';
import { changeFormData, changeState, validateForm } from '../../reducers';
import AgbInfo from '../agb-info/agb-info';
import InvoiceForm from './invoice-form/invoice-form';
import useActionUrl from './hooks/use-action-url';
import qualityEvents from '../../../../services/quality-events/quality-events';
import { PURCHASE_FORM_MODE } from './purchase-form.constants';
import { KUSHKI_ALLOWED_METHODS } from '../kushki/constants/kushki';
import { PAYMENT_METHOD_KEYS } from '../../../../constants/payment-method-keys';
import formatPriceToFloat from '../../../../utils/price/format-price-to-float';
import './purchase-form.scss';

type PurchaseFormProps = TranslationProps & {
    mode: PURCHASE_FORM_MODE;
};

const PurchaseForm = ({ mode, t }: PurchaseFormProps) => {
    const user = useSelector((state: RootState) => state.main.user);
    const {
        activePaymentOption,
        formSubmitAttempt,
        formData,
        formErrors,
        isSchoolMaster,
        paymentCart
    } = useAppSelector((state) => state.payment);

    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement | null>(null);

    const actionURL = useActionUrl(mode, activePaymentOption?.id);
    const checkEmail = useCheckEmail();

    const affectedFields =
        Object.keys(formErrors)
            .filter((item) => formErrors[item])
            .filter((item) => item !== 'email') || [];

    useAutocompleteInvoice(user);

    useEffect(() => {
        if (formSubmitAttempt && paymentCart?.loaded) {
            if (!activePaymentOption?.id && (formatPriceToFloat(paymentCart?.finalPrice) || 0) > 0) {
                return;
            }

            const isValid = dispatch(validateForm() as unknown as boolean);

            if (isValid && !isSchoolMaster) {
                dispatch(changeState({ isDataProcessing: true }));
                window.scrollTo(0, 0);

                if (formData.email && !emailHash.getStoredMD5()) {
                    emailHash.storeMD5(formData.email);
                    emailHash.storeSHA256(formData.email);
                }

                qualityEvents.checkoutPage.startPayment(
                    user.id,
                    getActivePaymentMethodKey(activePaymentOption?.id || '') || 'free',
                    paymentCart?.finalPrice
                ).then(() => {
                    if (!KUSHKI_ALLOWED_METHODS.includes(activePaymentOption?.id as PAYMENT_METHOD_KEYS)) {
                        if (mode === PURCHASE_FORM_MODE.PAID && activePaymentOption?.id) {
                            enhancedEcommerce.tracker.checkoutStepComplete(activePaymentOption.id);
                        }

                        formRef.current?.submit();
                    }
                });
            }
        }
    }, [formSubmitAttempt, paymentCart?.loaded]);

    useEffect(() => {
        const preventEnter = (event: KeyboardEvent) => {
            if (event.keyCode === 13) {
                event.preventDefault();

                return false;
            }
        };

        window.addEventListener('keydown', preventEnter);

        return () => {
            window.removeEventListener('keydown', preventEnter);
        };
    }, []);

    useEffect(() => {
        if (formErrors.email) {
            qualityEvents.checkoutPage.checkoutEmailValidationError();
        }
    }, [formErrors.email]);

    const changeModel = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(changeFormData({ [e.target.name]: e.target.value }));
    }

    const getFormName = () => {
        return mode === PURCHASE_FORM_MODE.FREE ? 'freePurchaseForm' : 'paymentForm';
    };

    return (
        <div className='payment-form'>
            {Boolean(affectedFields.length) && (
                <div className='mt-5'>
                    <Alert variant='danger'>
                        <h6 className='text-center'>{t('payment.paymentForm.incorrectForm')}</h6>
                    </Alert>
                </div>
            )}
            {isSchoolMaster && (
                <Alert variant='danger'>
                    <h6 className='text-center'>{t('payment.paymentForm.loginToBuy')}</h6>
                </Alert>
            )}
            <form
                ref={formRef}
                id='form'
                name={getFormName()}
                method='post'
                action={actionURL}
                autoComplete='on'
            >
                <div className={cn('email-field-wrapper', { 'd-none': session.isLoggedIn() })}>
                    <span className='lmp-regular'>
                        <b>{t('payment.paymentForm.emailAddress')} </b>
                        {t('payment.paymentForm.forPDF')}
                    </span>
                    <input
                        type='email'
                        name='email'
                        value={formData.email}
                        placeholder={t('payment.paymentForm.emailAddress')}
                        className={cn('form-control tsc-body', {
                            'error-field': formErrors.email || isSchoolMaster
                        })}
                        onChange={changeModel}
                        onBlur={checkEmail}
                    />
                    {formErrors.email && (
                        <span className='email-field-error'>
                            <FontAwesomeIcon height={24} name='warning-triangle' theme='red' />
                            {t('payment.paymentForm.emailFieldError')}
                        </span>
                    )}
                </div>
                <AgbInfo />
                <InvoiceForm />
                <span className='unsubscribe-info-container'>{t('payment.unsubscribeRecommendationsInfo')}</span>
                <input type='submit' className='fakeBtn' />
            </form>
        </div>
    );
};

const translate = translation('payment');

export default translate(PurchaseForm);
